import {
  formatProgressStepLabel,
  type OperationProgressModel,
  type OperationProgressStep,
  type OperationProgressStepStatus,
} from '../../../shared/ui/progress';

/**
 * Staged boot model for the full-workspace loading page shown while the
 * mapping workspace is still resolving its initial profile/project state.
 * Only flags that already exist on the page view model are wired here.
 */
export interface MappingWorkspaceLoadingInput {
  /** Waiting for the active Revit project identity before setup can load. */
  projectIdentityPending: boolean;
  /** Initial profile list fetch with no profile resolved yet. */
  profilesLoading: boolean;
  /** Trusted project profile is being loaded into this session. */
  restoringProjectProfile: boolean;
  /** Resolved profile name used for a friendlier restore message. */
  resolvedProfileName?: string;
}

const workspaceLoadingSteps = [
  {
    id: 'profiles',
    label: 'Loading mapping profiles',
    detail: 'Reading available User and Company PCF mapping profiles.',
  },
  {
    id: 'configuration',
    label: 'Loading associated profile',
    detail: 'Loading the selected or associated profile for this Revit project.',
  },
  {
    id: 'workspace',
    label: 'Preparing mapping editors',
    detail: 'Connecting PCF attribute targets to the active project’s Revit parameter sources.',
  },
] as const;

type WorkspaceLoadingStepId = (typeof workspaceLoadingSteps)[number]['id'];

function getWorkspaceLoadingStepStatus(
  stepId: WorkspaceLoadingStepId,
  input: MappingWorkspaceLoadingInput
): OperationProgressStepStatus {
  if (stepId === 'profiles') {
    return input.profilesLoading ? 'current' : 'complete';
  }

  if (stepId === 'configuration') {
    if (input.restoringProjectProfile) {
      return 'current';
    }

    return input.profilesLoading ? 'pending' : 'complete';
  }

  if (input.projectIdentityPending) {
    return 'current';
  }

  return input.profilesLoading || input.restoringProjectProfile ? 'pending' : 'complete';
}

function buildWorkspaceLoadingSteps(input: MappingWorkspaceLoadingInput): OperationProgressStep[] {
  return workspaceLoadingSteps.map((step) => ({
    id: step.id,
    label: step.label,
    detail: step.detail,
    status: getWorkspaceLoadingStepStatus(step.id, input),
  }));
}

function getActiveWorkspaceLoadingStepIndex(steps: OperationProgressStep[]): number {
  const activeIndex = steps.findIndex((step) => step.status === 'current');
  if (activeIndex >= 0) {
    return activeIndex;
  }

  const firstPendingIndex = steps.findIndex((step) => step.status === 'pending');
  return firstPendingIndex >= 0 ? firstPendingIndex : Math.max(0, steps.length - 1);
}

function buildWorkspaceLoadingMessage(input: MappingWorkspaceLoadingInput): string {
  if (input.profilesLoading) {
    return 'Loading available PCF mapping profiles';
  }

  if (input.restoringProjectProfile) {
    const profileName = input.resolvedProfileName?.trim();
    return profileName
      ? `Loading ${profileName}, the profile associated with this Revit project`
      : 'Loading the profile associated with this Revit project';
  }

  if (input.projectIdentityPending) {
    return 'Waiting for an active Revit project before preparing mapping editors';
  }

  return 'Preparing PCF mapping targets and Revit parameter sources';
}

export function buildMappingWorkspaceLoadingModel(
  input: MappingWorkspaceLoadingInput
): OperationProgressModel {
  const steps = buildWorkspaceLoadingSteps(input);
  const activeStepIndex = getActiveWorkspaceLoadingStepIndex(steps);

  return {
    title: 'Loading the PCF mapping workspace',
    message: buildWorkspaceLoadingMessage(input),
    detail: 'Editors open after PipeEX loads available profiles, resolves the associated profile, and connects Revit parameter sources.',
    status: 'running',
    percent: null,
    phase: 'Preparing mapping editors',
    stepLabel: formatProgressStepLabel(activeStepIndex + 1, steps.length, 1),
    locked: true,
    steps,
  };
}
